/**
 * Resource Availability dashboard.
 *
 * 12-month rolling view across all in-scope workers. Combines budgeted-hours-per-day allocation projections,
 * confirmed PTO, and stat holidays. Forecast assumes current allocations remain unchanged
 * through engagement end dates. The granularity toggle (monthly/weekly) is FE-only — the
 * backend always returns weekly cells; the FE aggregates to months on demand.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { usePageTitle } from '@/hooks/use-page-title'
import { useResourceAvailabilityData } from '@/hooks/use-resource-availability-data'
import { useSkillsCertificationsCatalog } from '@/hooks/use-skills-certifications-catalog'
import {
  defaultMonthWindow,
  useAvailabilityFilters,
} from '@/hooks/use-availability-filters'
import { AvailabilityKPICards } from '@/components/resource-availability/AvailabilityKPICards'
import { AvailabilityHeatmap } from '@/components/resource-availability/AvailabilityHeatmap'
import { HeatmapInfoTooltip } from '@/components/resource-availability/HeatmapInfoTooltip'
import { AvailabilityToggles } from '@/components/resource-availability/AvailabilityToggles'
import { ResourceAvailabilityFilterPane } from '@/components/resource-availability/ResourceAvailabilityFilterPane'
import { ActiveFilterPills } from '@/components/resource-availability/ActiveFilterPills'
import {
  TeamTimelineWaterfall,
  type TeamTimelineWorkerRow,
} from '@/components/resource-availability/TeamTimelineWaterfall'
import type { EngagementTimelineRow } from '@/components/resource-availability/EngagementTimelineWaterfall'
import type { TimelineMonth } from '@/lib/utils/timeline-axis'
import { Button } from '@/components/ui/button'
import { DatePicker } from '@/components/ui/date-picker'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { TablePagination } from '@/components/ui/table-pagination'
import { Download } from 'lucide-react'
import type { Granularity, MonthFilter, MonthFilterMap } from '@/types/ui/resource-availability'
import { exportToExcel } from '@/lib/utils/export-utils'
import { requestTokenFromParent } from '@/lib/app-messaging'
import { useLanguage } from '@/contexts/language-context'
import { useCodeValues } from '@/hooks/use-code-values'
import { matchesSkillAndProficiency, weekDayRange } from '@/lib/utils/availability-utils'
import { budgetedHoursLabel } from '@/lib/utils/budgeted-hours-timeline'

// Code-value group names for the three profile-sourced filters. Keep these as literals
// rather than importing CODE_TABLE_CATEGORIES because procurement source isn't in that
// constants file (it lives under `usr.*`, not `workorder.*` / `geo.*`).
const BRANCH_GROUP = 'workorder.CodeInternalOrganizationDefinition1'
const WORKER_LOCATION_GROUP = 'geo.CodeSubdivision'
const PROCUREMENT_SOURCE_GROUP = 'usr.CodeProcurementSource'

const WORKERS_PAGE_SIZE = 10

// What a worker skill carries when no proficiency level was recorded. No catalog entry uses it.
const NO_PROFICIENCY_LEVEL = 0

function defaultViewRange(startMonth: string, endMonth: string): { viewStart: string; viewEnd: string } {
  // Default = full data window: first day of startMonth to last day of endMonth.
  // Format yyyy-MM-dd so it matches `<input type="date">` and `bucketStart` comparisons.
  const end = new Date(endMonth)
  const lastDay = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + 1, 0))
  const fmt = (d: Date) =>
    `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
  return { viewStart: startMonth, viewEnd: fmt(lastDay) }
}

function weekEndIso(weekStartIso: string): string {
  // Sunday of the ISO week (Monday + 6 days).
  const d = new Date(weekStartIso + 'T00:00:00Z')
  const end = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + 6))
  return `${end.getUTCFullYear()}-${String(end.getUTCMonth() + 1).padStart(2, '0')}-${String(end.getUTCDate()).padStart(2, '0')}`
}

// Local-time ISO ↔ Date conversion for the DatePicker. Local time keeps the calendar's
// "May 1" matching the bucket comparison string "2026-05-01" without timezone drift.
function isoToLocalDate(iso: string): Date | undefined {
  if (!iso) return undefined
  const [y, m, d] = iso.split('-').map(Number)
  if (!y || !m || !d) return undefined
  return new Date(y, m - 1, d)
}
function localDateToIso(d: Date | null | undefined): string | undefined {
  if (!d) return undefined
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export default function ResourceAvailabilityPage() {
  const { dictionary } = useLanguage()
  const t = dictionary?.resourceAvailability?.page
  const tToggles = dictionary?.resourceAvailability?.toggles
  const tHeatmap = dictionary?.resourceAvailability?.heatmap
  const tTeam = dictionary?.resourceAvailability?.team
  usePageTitle(t?.title || 'Resource Availability')

  // Boot: ensure we have a bearer token from the parent shell.
  useEffect(() => {
    const t = setTimeout(() => requestTokenFromParent(), 100)
    return () => clearTimeout(t)
  }, [])

  const { startMonth, endMonth } = useMemo(() => defaultMonthWindow(), [])
  const { filters, setMode, setGranularity } = useAvailabilityFilters()
  const [page, setPage] = useState(1)
  const [monthFilters, setMonthFilters] = useState<MonthFilterMap>({})
  const initialRange = useMemo(() => defaultViewRange(startMonth, endMonth), [startMonth, endMonth])
  // The heatmap From/To pickers ARE the data window — no separate "view filter within
  // a fixed 12-month fetch" anymore. Pickers are uncapped; BE handles arbitrary spans.
  const [viewStart, setViewStart] = useState<string>(initialRange.viewStart)
  const [viewEnd, setViewEnd] = useState<string>(initialRange.viewEnd)
  // Debounced fetch range. Picker scrubbing updates `viewStart`/`viewEnd` instantly so the
  // input feels responsive; the network request only fires after the user pauses for 350ms.
  // Without debounce, every intermediate date during typing/scrubbing would trigger a fetch.
  const [fetchStart, setFetchStart] = useState<string>(initialRange.viewStart)
  const [fetchEnd, setFetchEnd] = useState<string>(initialRange.viewEnd)
  useEffect(() => {
    const handle = setTimeout(() => {
      setFetchStart(viewStart)
      setFetchEnd(viewEnd)
    }, 350)
    return () => clearTimeout(handle)
  }, [viewStart, viewEnd])
  const [nameFilter, setNameFilter] = useState('')
  const [selectedWorkerTypes, setSelectedWorkerTypes] = useState<string[]>([])
  const [selectedJobFunctions, setSelectedJobFunctions] = useState<string[]>([])
  const [selectedSkills, setSelectedSkills] = useState<string[]>([])
  const [selectedCertifications, setSelectedCertifications] = useState<string[]>([])
  const [selectedProficiencyLevels, setSelectedProficiencyLevels] = useState<string[]>([])
  const [selectedBranches, setSelectedBranches] = useState<string[]>([])
  const [selectedWorkerLocations, setSelectedWorkerLocations] = useState<string[]>([])
  const [selectedProcurementSources, setSelectedProcurementSources] = useState<string[]>([])
  const [selectedFunctionalManagers, setSelectedFunctionalManagers] = useState<string[]>([])
  const [selectedEngagements, setSelectedEngagements] = useState<string[]>([])

  const { data: workers, raw, loading, pendingRange, error } = useResourceAvailabilityData(fetchStart, fetchEnd)
  const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([])

  // Code → display-name resolvers for the three profile-sourced filters. The hook depends
  // on `isReady` (window.PhoenixCodeValues populated by CodeProvider on boot) — once flipped
  // the resolvers return real labels and the option-sort `useMemo` re-runs to alphabetize
  // by label.
  const { isReady: codesReady, getCodeValueTextByCode } = useCodeValues()
  const resolveBranchLabel = useCallback(
    (code: string) => getCodeValueTextByCode(code, BRANCH_GROUP) || code,
    [getCodeValueTextByCode, codesReady]
  )
  const resolveWorkerLocationLabel = useCallback(
    (code: string) => getCodeValueTextByCode(code, WORKER_LOCATION_GROUP) || code,
    [getCodeValueTextByCode, codesReady]
  )
  const resolveProcurementSourceLabel = useCallback(
    (code: string) => getCodeValueTextByCode(code, PROCUREMENT_SOURCE_GROUP) || code,
    [getCodeValueTextByCode, codesReady]
  )
  // Manager-name lookup driven by the workers themselves: each worker carries its manager's
  // resolved name (the data hook pre-fetches manager profiles so this is populated for
  // managers who aren't workers). Falls back to `Worker {id}` when a manager profile
  // hasn't resolved — same shape used elsewhere for unresolved profiles.
  const functionalManagerNamesById = useMemo(() => {
    const map = new Map<string, string>()
    for (const w of workers) {
      if (w.functionalManagerProfileId === undefined) continue
      const idStr = String(w.functionalManagerProfileId)
      if (!map.has(idStr) && w.functionalManagerName) map.set(idStr, w.functionalManagerName)
    }
    return map
  }, [workers])
  const resolveFunctionalManagerLabel = useCallback(
    (idStr: string) => functionalManagerNamesById.get(idStr) ?? `Worker ${idStr}`,
    [functionalManagerNamesById]
  )
  // Filter dropdown options are the full active catalog, so an entry no worker holds yet is
  // still selectable, plus any further id the loaded workers hold.
  const skillsCatalog = useSkillsCertificationsCatalog()
  const resolveSkillLabel = useCallback(
    (idStr: string) => skillsCatalog.getSkillName(Number(idStr)) ?? `Skill ${idStr}`,
    [skillsCatalog]
  )
  const resolveCertificationLabel = useCallback(
    (idStr: string) => skillsCatalog.getCertificationName(Number(idStr)) ?? `Certification ${idStr}`,
    [skillsCatalog]
  )
  const resolveProficiencyLevelLabel = useCallback(
    (idStr: string) => skillsCatalog.getProficiencyLevelName(Number(idStr)) ?? `Proficiency Level ${idStr}`,
    [skillsCatalog]
  )
  // Engagement recordId → display label. Prefixes the recordId so users can eyeball/search
  // by id, mirroring the other engagement dropdowns (which show "id.version - code: name").
  // Falls back to "Engagement {id}" when the name isn't in the payload.
  const resolveEngagementLabel = useCallback(
    (id: string) => {
      const name = raw?.engagementNamesById?.[id]
      return name ? `${id} - ${name}` : `Engagement ${id}`
    },
    [raw]
  )
  // Independent granularity for the team waterfall (not synced with the heatmap toggle).
  // Each section can be in a different view mode — heatmap weekly for tactical detail,
  // waterfall monthly for high-level Gantt, or vice versa.
  const [waterfallGranularity, setWaterfallGranularity] = useState<Granularity>('monthly')
  // Independent view range for the waterfall (also separate from the heatmap's range).
  // Defaults to the full data window; narrowing slices the timeline axis and clips bars.
  const [waterfallViewStart, setWaterfallViewStart] = useState<string>(initialRange.viewStart)
  const [waterfallViewEnd, setWaterfallViewEnd] = useState<string>(initialRange.viewEnd)
  // Clamp waterfall view-range into the current data window. If the user shrinks the
  // heatmap range so the waterfall range pokes outside, snap it back so the pickers and
  // the rendered axis stay coherent. Computes both targets first so a wholesale shift
  // (heatmap moves entirely past the prior waterfall range) doesn't leave the waterfall
  // in an invalid `start > end` state — that would empty the axis filter and the
  // chart would render nothing.
  useEffect(() => {
    const clampedStart =
      waterfallViewStart < viewStart ? viewStart
      : waterfallViewStart > viewEnd ? viewEnd
      : waterfallViewStart
    const clampedEnd =
      waterfallViewEnd > viewEnd ? viewEnd
      : waterfallViewEnd < viewStart ? viewStart
      : waterfallViewEnd
    const orderedStart = clampedStart > clampedEnd ? clampedEnd : clampedStart
    if (orderedStart !== waterfallViewStart) setWaterfallViewStart(orderedStart)
    if (clampedEnd !== waterfallViewEnd) setWaterfallViewEnd(clampedEnd)
  }, [viewStart, viewEnd, waterfallViewStart, waterfallViewEnd])

  // Distinct workerType / jobFunction labels from the full result set. Stays stable as
  // the user toggles checkboxes — options don't disappear once selected.
  const workerTypeOptions = useMemo(() => {
    const set = new Set<string>()
    for (const w of workers) if (w.workerRole) set.add(w.workerRole)
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [workers])
  const jobFunctionOptions = useMemo(() => {
    const set = new Set<string>()
    for (const w of workers) for (const jf of w.jobFunctions ?? []) set.add(jf)
    return Array.from(set).sort((a, b) => a.localeCompare(b))
  }, [workers])
  // Sourced from the full active catalog, unioned with whatever ids workers actually hold as
  // a fallback for an entry not yet in the catalog. Sorted alphabetically by resolved label.
  const skillOptions = useMemo(() => {
    const set = new Set(skillsCatalog.skills.map(s => String(s.id)))
    for (const w of workers) for (const s of w.skills ?? []) set.add(String(s.skillId))
    return Array.from(set).sort((a, b) => resolveSkillLabel(a).localeCompare(resolveSkillLabel(b)))
  }, [workers, skillsCatalog, resolveSkillLabel])
  const certificationOptions = useMemo(() => {
    const set = new Set(skillsCatalog.certifications.map(c => String(c.id)))
    for (const w of workers) for (const c of w.certifications ?? []) set.add(String(c.certificationId))
    return Array.from(set).sort((a, b) => resolveCertificationLabel(a).localeCompare(resolveCertificationLabel(b)))
  }, [workers, skillsCatalog, resolveCertificationLabel])
  const proficiencyLevelOptions = useMemo(() => {
    const set = new Set(skillsCatalog.proficiencyLevels.map(p => String(p.id)))
    for (const w of workers) for (const s of w.skills ?? []) {
      if (s.proficiencyLevelId !== undefined && s.proficiencyLevelId !== NO_PROFICIENCY_LEVEL) {
        set.add(String(s.proficiencyLevelId))
      }
    }
    return Array.from(set).sort((a, b) => resolveProficiencyLevelLabel(a).localeCompare(resolveProficiencyLevelLabel(b)))
  }, [workers, skillsCatalog, resolveProficiencyLevelLabel])
  // Sort code-backed options by their resolved label so users see "Toronto" before "Vancouver"
  // rather than "TOR" before "VAN". Falls back to the raw code if a label isn't resolvable.
  const branchOptions = useMemo(() => {
    const set = new Set<string>()
    for (const w of workers) if (w.branchCode) set.add(w.branchCode)
    return Array.from(set).sort((a, b) => resolveBranchLabel(a).localeCompare(resolveBranchLabel(b)))
  }, [workers, resolveBranchLabel])
  const workerLocationOptions = useMemo(() => {
    const set = new Set<string>()
    for (const w of workers) if (w.workerLocation) set.add(w.workerLocation)
    return Array.from(set).sort((a, b) => resolveWorkerLocationLabel(a).localeCompare(resolveWorkerLocationLabel(b)))
  }, [workers, resolveWorkerLocationLabel])
  const procurementSourceOptions = useMemo(() => {
    const set = new Set<string>()
    for (const w of workers) if (w.procurementSourceCode) set.add(w.procurementSourceCode)
    return Array.from(set).sort((a, b) => resolveProcurementSourceLabel(a).localeCompare(resolveProcurementSourceLabel(b)))
  }, [workers, resolveProcurementSourceLabel])
  const functionalManagerOptions = useMemo(() => {
    const set = new Set<string>()
    for (const w of workers) {
      if (w.functionalManagerProfileId !== undefined) set.add(String(w.functionalManagerProfileId))
    }
    return Array.from(set).sort((a, b) => resolveFunctionalManagerLabel(a).localeCompare(resolveFunctionalManagerLabel(b)))
  }, [workers, resolveFunctionalManagerLabel])
  // Engagement filter options = EVERY in-window engagement the backend loaded
  // (engagementNamesById), not just those with budgeted-hour allocation on a visible
  // worker. The per-cell activeEngagementIds are allocation-sparse (the BE drops weeks with
  // zero budgeted hours), so deriving options from worker cells alone hides engagements that
  // exist in the window but have no allocated hours yet. Union with the worker-derived ids
  // as a fallback for an older BE that doesn't send engagementNamesById. Sorted by name.
  const engagementOptions = useMemo(() => {
    const nameOf = (id: string) => raw?.engagementNamesById?.[id] ?? ''
    const set = new Set<string>(Object.keys(raw?.engagementNamesById ?? {}))
    for (const w of workers) for (const id of w.engagementIds ?? []) set.add(id)
    return Array.from(set).sort((a, b) => nameOf(a).localeCompare(nameOf(b)) || a.localeCompare(b))
  }, [workers, raw])

  // Heatmap filter columns track the *displayed* cells. Index → cell mapping flips with
  // granularity AND view range, which is why we clear monthFilters on those changes.
  const cellsForFilter = (w: typeof workers[number]) => {
    const base = filters.granularity === 'weekly' ? w.weeklyCells : w.months
    return base.filter(c => {
      if (viewStart && c.monthStart < viewStart) return false
      if (viewEnd && c.monthStart > viewEnd) return false
      return true
    })
  }

  // Apply name + worker-type + job-function + per-month filters. Month filters test against
  // whatever percent the user is currently viewing so users filter the values they actually
  // see in the cells.
  const filteredWorkers = useMemo(() => {
    const monthEntries = Object.entries(monthFilters)
    const nameNeedle = nameFilter.trim().toLowerCase()
    const typeSet = new Set(selectedWorkerTypes)
    const fnSet = new Set(selectedJobFunctions)
    const skillSet = new Set(selectedSkills)
    const certSet = new Set(selectedCertifications)
    const proficiencyLevelSet = new Set(selectedProficiencyLevels)
    const branchSet = new Set(selectedBranches)
    const locationSet = new Set(selectedWorkerLocations)
    const procurementSet = new Set(selectedProcurementSources)
    const managerSet = new Set(selectedFunctionalManagers)
    const engagementSet = new Set(selectedEngagements)
    const noFilters =
      monthEntries.length === 0 &&
      !nameNeedle &&
      typeSet.size === 0 &&
      fnSet.size === 0 &&
      skillSet.size === 0 &&
      certSet.size === 0 &&
      proficiencyLevelSet.size === 0 &&
      branchSet.size === 0 &&
      locationSet.size === 0 &&
      procurementSet.size === 0 &&
      managerSet.size === 0 &&
      engagementSet.size === 0
    if (noFilters) return workers
    return workers.filter(w => {
      if (nameNeedle && !w.workerName.toLowerCase().includes(nameNeedle)) return false
      if (typeSet.size > 0 && !(w.workerRole && typeSet.has(w.workerRole))) return false
      if (fnSet.size > 0) {
        const jfs = w.jobFunctions ?? []
        if (!jfs.some(jf => fnSet.has(jf))) return false
      }
      // ANY-match within a section, AND-match across sections. Skills and proficiency levels are
      // one section for this purpose: they pair on the same skill entry rather than being
      // satisfied independently. Levels are exact, not "at this level or above" — the catalog
      // carries no ordinal ranking to compare against.
      if (!matchesSkillAndProficiency(w.skills, skillSet, proficiencyLevelSet)) return false
      // An expired certification still matches; it's flagged, not filtered out.
      if (certSet.size > 0) {
        const certs = w.certifications ?? []
        if (!certs.some(c => certSet.has(String(c.certificationId)))) return false
      }
      if (branchSet.size > 0) {
        if (!w.branchCode || !branchSet.has(w.branchCode)) return false
      }
      if (locationSet.size > 0) {
        if (!w.workerLocation || !locationSet.has(w.workerLocation)) return false
      }
      if (procurementSet.size > 0) {
        if (!w.procurementSourceCode || !procurementSet.has(w.procurementSourceCode)) return false
      }
      if (managerSet.size > 0) {
        if (w.functionalManagerProfileId === undefined || !managerSet.has(String(w.functionalManagerProfileId))) return false
      }
      // Engagement: worker passes if they're on ANY selected engagement (returns the
      // whole team of that engagement). AND-matched against the other sections.
      if (engagementSet.size > 0) {
        const eids = w.engagementIds ?? []
        if (!eids.some(id => engagementSet.has(id))) return false
      }
      const filterCells = cellsForFilter(w)
      for (const [idxStr, f] of monthEntries) {
        const cell = filterCells[Number(idxStr)]
        // Fail closed: a missing cell means we can't verify the filter, so exclude the
        // worker rather than silently letting them through.
        if (!cell) return false
        const value = filters.mode === 'allocated' ? cell.allocatedPercent : cell.availablePercent
        const ok = f.op === 'gte' ? value >= f.value : value <= f.value
        if (!ok) return false
      }
      return true
    })
  }, [
    workers,
    monthFilters,
    nameFilter,
    selectedWorkerTypes,
    selectedJobFunctions,
    selectedSkills,
    selectedCertifications,
    selectedProficiencyLevels,
    selectedBranches,
    selectedWorkerLocations,
    selectedProcurementSources,
    selectedFunctionalManagers,
    selectedEngagements,
    filters.mode,
    filters.granularity,
  ])

  // Heatmap is the only paginated view — KPI cards keep the full list since they aggregate.
  const pagedWorkers = useMemo(() => {
    const start = (page - 1) * WORKERS_PAGE_SIZE
    return filteredWorkers.slice(start, start + WORKERS_PAGE_SIZE)
  }, [filteredWorkers, page])

  // If the result set shrinks (filter change, fewer workers in scope), keep page in range.
  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(filteredWorkers.length / WORKERS_PAGE_SIZE))
    if (page > totalPages) setPage(totalPages)
  }, [filteredWorkers.length, page])

  const handleModeChange = (mode: typeof filters.mode) => {
    // Month-column filters are tested against whichever percent the user is currently
    // viewing (allocated vs available), so a "≥ 75%" filter means inverse things in each
    // mode. Clearing all filters on mode switch keeps the post-toggle view predictable.
    setMonthFilters({})
    setNameFilter('')
    setSelectedWorkerTypes([])
    setSelectedJobFunctions([])
    setSelectedSkills([])
    setSelectedCertifications([])
    setSelectedProficiencyLevels([])
    setSelectedBranches([])
    setSelectedWorkerLocations([])
    setSelectedProcurementSources([])
    setSelectedFunctionalManagers([])
    setSelectedEngagements([])
    setPage(1)
    setMode(mode)
  }

  const handleGranularityChange = (granularity: Granularity) => {
    // monthFilters keys are column indexes; weekly and monthly indexes don't align,
    // so clear them on granularity change to avoid filtering the wrong column.
    setMonthFilters({})
    setPage(1)
    setGranularity(granularity)
  }

  const handleViewStartChange = (next: string) => {
    setViewStart(next)
    // Range narrowing shifts column indexes — clear monthFilters to keep semantics clean.
    setMonthFilters({})
    setPage(1)
  }
  const handleViewEndChange = (next: string) => {
    setViewEnd(next)
    setMonthFilters({})
    setPage(1)
  }

  const handleMonthFilterChange = (monthIndex: number, filter: MonthFilter | undefined) => {
    setMonthFilters(prev => {
      const next = { ...prev }
      if (filter) next[monthIndex] = filter
      else delete next[monthIndex]
      return next
    })
    setPage(1)
  }
  const handleNameFilterChange = (value: string) => {
    setNameFilter(value)
    setPage(1)
  }
  const handleWorkerTypesChange = (next: string[]) => {
    setSelectedWorkerTypes(next)
    setPage(1)
  }
  const handleJobFunctionsChange = (next: string[]) => {
    setSelectedJobFunctions(next)
    setPage(1)
  }
  const handleSkillsChange = (next: string[]) => {
    setSelectedSkills(next)
    setPage(1)
  }
  const handleCertificationsChange = (next: string[]) => {
    setSelectedCertifications(next)
    setPage(1)
  }
  const handleProficiencyLevelsChange = (next: string[]) => {
    setSelectedProficiencyLevels(next)
    setPage(1)
  }
  const handleBranchesChange = (next: string[]) => {
    setSelectedBranches(next)
    setPage(1)
  }
  const handleWorkerLocationsChange = (next: string[]) => {
    setSelectedWorkerLocations(next)
    setPage(1)
  }
  const handleProcurementSourcesChange = (next: string[]) => {
    setSelectedProcurementSources(next)
    setPage(1)
  }
  const handleFunctionalManagersChange = (next: string[]) => {
    setSelectedFunctionalManagers(next)
    setPage(1)
  }
  const handleEngagementsChange = (next: string[]) => {
    setSelectedEngagements(next)
    setPage(1)
  }
  const resetAllPaneFilters = () => {
    setSelectedWorkerTypes([])
    setSelectedJobFunctions([])
    setSelectedSkills([])
    setSelectedCertifications([])
    setSelectedProficiencyLevels([])
    setSelectedBranches([])
    setSelectedWorkerLocations([])
    setSelectedProcurementSources([])
    setSelectedFunctionalManagers([])
    setSelectedEngagements([])
    setPage(1)
  }

  const handleToggleTeamMember = (userProfileId: number) => {
    setSelectedTeamIds(prev =>
      prev.includes(userProfileId) ? prev.filter(id => id !== userProfileId) : [...prev, userProfileId]
    )
  }

  // Header select-all toggle: scoped to the entire filtered set, not just the current
  // page. Users expect "select all" to mean every worker matching their filters, not
  // whichever 10 rows the heatmap is paging through right now.
  const filteredWorkerIds = useMemo(
    () => filteredWorkers.map(w => w.userProfileId),
    [filteredWorkers]
  )
  const allFilteredSelected = useMemo(() => {
    if (filteredWorkerIds.length === 0) return false
    const sel = new Set(selectedTeamIds)
    return filteredWorkerIds.every(id => sel.has(id))
  }, [filteredWorkerIds, selectedTeamIds])
  const handleToggleAll = () => {
    setSelectedTeamIds(prev => {
      if (allFilteredSelected) {
        const remove = new Set(filteredWorkerIds)
        return prev.filter(id => !remove.has(id))
      }
      return Array.from(new Set([...prev, ...filteredWorkerIds]))
    })
  }

  // Build the team timeline rows from selected ids + the raw response. Empty engagements
  // array means the worker is fully available across the window.
  const teamRows = useMemo<TeamTimelineWorkerRow[]>(() => {
    if (selectedTeamIds.length === 0) return []
    const byId = new Map(workers.map(w => [w.userProfileId, w]))
    return selectedTeamIds.flatMap(id => {
      const worker = byId.get(id)
      if (!worker) return []
      const seen = new Set<string>()
      for (const cell of worker.months) {
        for (const eid of cell.activeEngagementIds) seen.add(eid)
      }
      const bhpdMap = raw?.workerEngagementBudgetedHoursPerDay?.[String(id)]
      const timelinesMap = raw?.workerEngagementBudgetedHoursTimelines?.[String(id)]
      const spansMap = raw?.workerEngagementSpans?.[String(id)]
      const engagements: EngagementTimelineRow[] = []
      for (const eid of seen) {
        const dates = raw?.engagementDates?.[eid]
        if (!dates) continue
        const span = spansMap?.[eid]
        engagements.push({
          engagementId: eid,
          engagementName: raw?.engagementNamesById?.[eid],
          engagementStart: dates.startDate,
          engagementEnd: dates.endDate,
          workerStart: span?.workerStart ?? null,
          workerEnd: span?.workerEnd ?? null,
          budgetedHoursPerDay: bhpdMap?.[eid],
          budgetedHoursLabel: budgetedHoursLabel(
            bhpdMap?.[eid],
            timelinesMap?.[eid],
            span?.workerStart ?? dates.startDate,
            span?.workerEnd ?? dates.endDate
          ),
        })
      }
      return [{
        userProfileId: id,
        workerName: worker.workerName,
        workerInitials: worker.workerInitials,
        workerRole: worker.workerRole,
        engagements,
      }]
    })
  }, [selectedTeamIds, workers, raw])

  const toggleQuery = useMemo(() => {
    const params = new URLSearchParams()
    params.set('mode', filters.mode)
    return params.toString()
  }, [filters])

  // Header cells for the heatmap. Stable ref via the first worker's cells (or empty)
  // matters less when the toggle is FE-only, but pinning to workers[0] keeps the column
  // structure consistent across renders even if pagedWorkers is empty. Apply the view
  // range here so the per-column filter popover targets the visible column, not the full
  // axis.
  const heatmapHeaderCells = useMemo(() => {
    const w = workers[0]
    if (!w) return undefined
    const base = filters.granularity === 'weekly' ? w.weeklyCells : w.months
    return base.filter(c => {
      if (viewStart && c.monthStart < viewStart) return false
      if (viewEnd && c.monthStart > viewEnd) return false
      return true
    })
  }, [workers, filters.granularity, viewStart, viewEnd])

  const handleExport = () => {
    if (workers.length === 0) return
    const rows = workers.flatMap(w =>
      w.months.map(m => ({
        Worker: w.workerName,
        Month: m.monthLabel,
        Capacity: m.capacityHours,
        Allocated: m.plannedHours,
        Stat: m.statHours,
        PTO: m.ptoHours,
        'Alloc %': m.allocatedPercent,
      }))
    )
    exportToExcel(rows, `resource-availability-${startMonth}.xlsx`, 'Resource Availability')
  }

  return (
    <div className="container mx-auto px-4 md:px-7 py-6 max-w-[1320px] space-y-5">
      <header className="space-y-1">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t?.title || 'Resource availability'}</h1>
            <p className="text-xs text-muted-foreground">
              {t?.subtitle || 'Real-time resource availability tracking · 12-month rolling forecast'}{' '}
            </p>
          </div>
          {/* Mode toggle (allocated vs available) and view-range pickers sit together up
              top — both feed the KPI cards directly, so they're co-located above the
              cards. The heatmap and waterfall consume the same view range; the waterfall
              has its own independent pickers further down. Heatmap-display-only controls
              (filters, export, granularity) stay in the heatmap section header. */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="inline-flex items-center gap-1.5">
              <span className="text-[11px] text-muted-foreground">{tHeatmap?.viewRangeFromLabel || 'From'}</span>
              <DatePicker
                value={isoToLocalDate(viewStart)}
                onChange={(d) => handleViewStartChange(localDateToIso(d) ?? initialRange.viewStart)}
                maxDate={isoToLocalDate(viewEnd)}
                allowClear={false}
                aria-label={tHeatmap?.viewRangeFromAria || 'Filter heatmap from date'}
                className="h-8 text-xs px-2 w-[9.5rem]"
              />
            </div>
            <div className="inline-flex items-center gap-1.5">
              <span className="text-[11px] text-muted-foreground">{tHeatmap?.viewRangeToLabel || 'To'}</span>
              <DatePicker
                value={isoToLocalDate(viewEnd)}
                onChange={(d) => handleViewEndChange(localDateToIso(d) ?? initialRange.viewEnd)}
                minDate={isoToLocalDate(viewStart)}
                allowClear={false}
                aria-label={tHeatmap?.viewRangeToAria || 'Filter heatmap to date'}
                className="h-8 text-xs px-2 w-[9.5rem]"
              />
            </div>
            <AvailabilityToggles filters={filters} onModeChange={handleModeChange} />
          </div>
        </div>
      </header>

      {error ? (
        <div className="rounded-md border border-red-300 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      ) : loading ? (
        <div className="rounded-md border bg-card p-8 text-center text-sm text-muted-foreground">
          {t?.loading || 'Loading resource availability…'}
        </div>
      ) : (
        <>
          <AvailabilityKPICards
            workers={workers}
            mode={filters.mode}
            viewStart={viewStart}
            viewEnd={viewEnd}
          />

          <section>
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-1.5">
                <h2 className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                  {filters.mode === 'allocated'
                    ? (t?.heatmapTitleAllocated || 'Allocation heatmap')
                    : (t?.heatmapTitleAvailable || 'Availability heatmap')}
                </h2>
                {/* Strings live under resourceAvailability.heatmap — a sibling of `page` (`t`),
                    not a child of it, so this must read tHeatmap or every label silently falls
                    back to its English default. */}
                <HeatmapInfoTooltip strings={tHeatmap} />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* View range now lives in the page header (co-located with the mode
                    toggle since both feed the KPI cards). Heatmap section keeps display
                    controls only — granularity, filters, export. */}
                <ToggleGroup
                  type="single"
                  value={filters.granularity}
                  onValueChange={(v: string) => v && handleGranularityChange(v as Granularity)}
                  aria-label={tToggles?.granularityAria || 'Heatmap granularity'}
                  className="bg-muted rounded-md p-0.5 border-0 h-8"
                >
                  <ToggleGroupItem
                    value="monthly"
                    className="text-xs px-2.5 h-7 border-0 text-foreground/70 hover:text-foreground hover:bg-transparent data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm data-[state=on]:font-medium"
                  >
                    {tToggles?.granularityMonthly || 'Monthly'}
                  </ToggleGroupItem>
                  <ToggleGroupItem
                    value="weekly"
                    className="text-xs px-2.5 h-7 border-0 text-foreground/70 hover:text-foreground hover:bg-transparent data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm data-[state=on]:font-medium"
                  >
                    {tToggles?.granularityWeekly || 'Weekly'}
                  </ToggleGroupItem>
                </ToggleGroup>
                <ResourceAvailabilityFilterPane
                  workerTypeOptions={workerTypeOptions}
                  jobFunctionOptions={jobFunctionOptions}
                  skillOptions={skillOptions}
                  certificationOptions={certificationOptions}
                  proficiencyLevelOptions={proficiencyLevelOptions}
                  selectedWorkerTypes={selectedWorkerTypes}
                  selectedJobFunctions={selectedJobFunctions}
                  selectedSkills={selectedSkills}
                  selectedCertifications={selectedCertifications}
                  selectedProficiencyLevels={selectedProficiencyLevels}
                  onWorkerTypesChange={handleWorkerTypesChange}
                  onJobFunctionsChange={handleJobFunctionsChange}
                  onSkillsChange={handleSkillsChange}
                  onCertificationsChange={handleCertificationsChange}
                  onProficiencyLevelsChange={handleProficiencyLevelsChange}
                  branchOptions={branchOptions}
                  workerLocationOptions={workerLocationOptions}
                  procurementSourceOptions={procurementSourceOptions}
                  selectedBranches={selectedBranches}
                  selectedWorkerLocations={selectedWorkerLocations}
                  selectedProcurementSources={selectedProcurementSources}
                  onBranchesChange={handleBranchesChange}
                  onWorkerLocationsChange={handleWorkerLocationsChange}
                  onProcurementSourcesChange={handleProcurementSourcesChange}
                  functionalManagerOptions={functionalManagerOptions}
                  selectedFunctionalManagers={selectedFunctionalManagers}
                  onFunctionalManagersChange={handleFunctionalManagersChange}
                  engagementOptions={engagementOptions}
                  selectedEngagements={selectedEngagements}
                  onEngagementsChange={handleEngagementsChange}
                  resolveEngagementLabel={resolveEngagementLabel}
                  resolveBranchLabel={resolveBranchLabel}
                  resolveWorkerLocationLabel={resolveWorkerLocationLabel}
                  resolveProcurementSourceLabel={resolveProcurementSourceLabel}
                  resolveFunctionalManagerLabel={resolveFunctionalManagerLabel}
                  resolveSkillLabel={resolveSkillLabel}
                  resolveCertificationLabel={resolveCertificationLabel}
                  resolveProficiencyLevelLabel={resolveProficiencyLevelLabel}
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={handleExport}
                  disabled={workers.length === 0}
                >
                  <Download className="w-4 mr-1.5" />
                  {t?.export || 'Export'}
                </Button>
              </div>
            </div>
            <ActiveFilterPills
              selectedWorkerTypes={selectedWorkerTypes}
              selectedJobFunctions={selectedJobFunctions}
              selectedSkills={selectedSkills}
              selectedCertifications={selectedCertifications}
              selectedProficiencyLevels={selectedProficiencyLevels}
              selectedBranches={selectedBranches}
              selectedWorkerLocations={selectedWorkerLocations}
              selectedProcurementSources={selectedProcurementSources}
              selectedFunctionalManagers={selectedFunctionalManagers}
              selectedEngagements={selectedEngagements}
              onWorkerTypesChange={handleWorkerTypesChange}
              onJobFunctionsChange={handleJobFunctionsChange}
              onSkillsChange={handleSkillsChange}
              onCertificationsChange={handleCertificationsChange}
              onProficiencyLevelsChange={handleProficiencyLevelsChange}
              onBranchesChange={handleBranchesChange}
              onWorkerLocationsChange={handleWorkerLocationsChange}
              onProcurementSourcesChange={handleProcurementSourcesChange}
              onFunctionalManagersChange={handleFunctionalManagersChange}
              onEngagementsChange={handleEngagementsChange}
              onResetAll={resetAllPaneFilters}
              resolveBranchLabel={resolveBranchLabel}
              resolveWorkerLocationLabel={resolveWorkerLocationLabel}
              resolveProcurementSourceLabel={resolveProcurementSourceLabel}
              resolveFunctionalManagerLabel={resolveFunctionalManagerLabel}
              resolveEngagementLabel={resolveEngagementLabel}
              resolveSkillLabel={resolveSkillLabel}
              resolveCertificationLabel={resolveCertificationLabel}
              resolveProficiencyLevelLabel={resolveProficiencyLevelLabel}
            />
            <AvailabilityHeatmap
              workers={pagedWorkers}
              mode={filters.mode}
              granularity={filters.granularity}
              toggleQuery={toggleQuery}
              monthFilters={monthFilters}
              monthLabelsForFilters={heatmapHeaderCells}
              onMonthFilterChange={handleMonthFilterChange}
              selectedTeamIds={selectedTeamIds}
              onToggleTeamMember={handleToggleTeamMember}
              allSelected={allFilteredSelected}
              onToggleAll={handleToggleAll}
              nameFilter={nameFilter}
              onNameFilterChange={handleNameFilterChange}
              viewStart={viewStart}
              viewEnd={viewEnd}
              pendingRange={pendingRange}
              resolveCertificationLabel={resolveCertificationLabel}
              selectedCertifications={selectedCertifications}
            />
            <TablePagination
              currentPage={page}
              totalCount={filteredWorkers.length}
              pageSize={WORKERS_PAGE_SIZE}
              onPageChange={setPage}
            />
          </section>

          {teamRows.length > 0 && workers[0] && (() => {
            // Build the axis buckets the waterfall renders. Monthly mode uses
            // workers[0].months (one cell per month). Weekly mode uses weeklyCells with
            // explicit endIso (Sunday). Both paths slice by the waterfall's view range
            // first; granularity-specific labels are computed by the waterfall itself
            // (it groups weekly cells into a top-tier month header).
            // Test bucket *overlap* with the view range — not just containment of the
            // bucket's start. The original `inRange(c.monthStart)` filter dropped every
            // cell when the view range didn't contain a Monday (e.g. a single-month view
            // whose only included weeks are "owned" by the prior month's Monday), leaving
            // an empty axis and the chart rendering nothing.
            const overlapsView = (start: string, end: string): boolean => {
              if (waterfallViewStart && end < waterfallViewStart) return false
              if (waterfallViewEnd && start > waterfallViewEnd) return false
              return true
            }
            const monthEndIso = (monthStartIso: string): string => {
              const d = new Date(monthStartIso + 'T00:00:00Z')
              const last = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0))
              return `${last.getUTCFullYear()}-${String(last.getUTCMonth() + 1).padStart(2, '0')}-${String(last.getUTCDate()).padStart(2, '0')}`
            }
            const axisBuckets: TimelineMonth[] =
              waterfallGranularity === 'weekly'
                ? workers[0].weeklyCells
                    .filter(c => overlapsView(c.monthStart, weekEndIso(c.monthStart)))
                    .map(c => ({
                      monthStart: c.monthStart,
                      // Per-cell label = day range (e.g. "4–10"). Top-tier month name is
                      // rendered separately by the waterfall, mirroring the heatmap.
                      monthLabel: weekDayRange(c.monthStart),
                      isCurrentMonth: c.isCurrentMonth,
                      endIso: weekEndIso(c.monthStart),
                    }))
                : workers[0].months
                    .filter(m => overlapsView(m.monthStart, monthEndIso(m.monthStart)))
                    .map(m => ({
                      monthStart: m.monthStart,
                      monthLabel: m.monthLabel,
                      isCurrentMonth: m.isCurrentMonth,
                    }))
            return (
              <section>
                <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                  <h2 className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                    {teamRows.length === 1
                      ? (tTeam?.teamTimelineHeadingSingular || 'Team timeline · 1 worker')
                      : (tTeam?.teamTimelineHeadingPlural || 'Team timeline · {n} workers').replace('{n}', String(teamRows.length))}
                  </h2>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Waterfall pickers are view-only — they slice within whatever the
                        heatmap fetched. Bounds track the *current* data window (viewStart /
                        viewEnd), not the original 12-month default, so they stay sane after
                        the heatmap range expands or shrinks. */}
                    <div className="inline-flex items-center gap-1.5">
                      <span className="text-[11px] text-muted-foreground">{tHeatmap?.viewRangeFromLabel || 'From'}</span>
                      <DatePicker
                        value={isoToLocalDate(waterfallViewStart)}
                        onChange={(d) => setWaterfallViewStart(localDateToIso(d) ?? viewStart)}
                        minDate={isoToLocalDate(viewStart)}
                        maxDate={isoToLocalDate(waterfallViewEnd || viewEnd)}
                        allowClear={false}
                        aria-label={tTeam?.viewRangeFromAria || 'Filter team timeline from date'}
                        className="h-8 text-xs px-2 w-[9.5rem]"
                      />
                    </div>
                    <div className="inline-flex items-center gap-1.5">
                      <span className="text-[11px] text-muted-foreground">{tHeatmap?.viewRangeToLabel || 'To'}</span>
                      <DatePicker
                        value={isoToLocalDate(waterfallViewEnd)}
                        onChange={(d) => setWaterfallViewEnd(localDateToIso(d) ?? viewEnd)}
                        minDate={isoToLocalDate(waterfallViewStart || viewStart)}
                        maxDate={isoToLocalDate(viewEnd)}
                        allowClear={false}
                        aria-label={tTeam?.viewRangeToAria || 'Filter team timeline to date'}
                        className="h-8 text-xs px-2 w-[9.5rem]"
                      />
                    </div>
                    <ToggleGroup
                      type="single"
                      value={waterfallGranularity}
                      onValueChange={(v: string) => v && setWaterfallGranularity(v as Granularity)}
                      aria-label={tToggles?.granularityAria || 'Waterfall granularity'}
                      className="bg-muted rounded-md p-0.5 border-0 h-8"
                    >
                      <ToggleGroupItem
                        value="monthly"
                        className="text-xs px-2.5 h-7 border-0 text-foreground/70 hover:text-foreground hover:bg-transparent data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm data-[state=on]:font-medium"
                      >
                        {tToggles?.granularityMonthly || 'Monthly'}
                      </ToggleGroupItem>
                      <ToggleGroupItem
                        value="weekly"
                        className="text-xs px-2.5 h-7 border-0 text-foreground/70 hover:text-foreground hover:bg-transparent data-[state=on]:bg-background data-[state=on]:text-foreground data-[state=on]:shadow-sm data-[state=on]:font-medium"
                      >
                        {tToggles?.granularityWeekly || 'Weekly'}
                      </ToggleGroupItem>
                    </ToggleGroup>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => setSelectedTeamIds([])}
                    >
                      {tTeam?.clearSelection || 'Clear selection'}
                    </Button>
                  </div>
                </div>
                <TeamTimelineWaterfall
                  workers={teamRows}
                  months={axisBuckets}
                  granularity={waterfallGranularity}
                  onRemoveWorker={handleToggleTeamMember}
                  axisMaxEndIso={waterfallViewEnd}
                />
              </section>
            )
          })()}

          <p className="text-xs text-muted-foreground">
            {t?.utilizationLinkPrefix || 'Looking for utilization trends?'}{' '}
            <Link to="/dashboard" className="text-blue-600 hover:underline">
              {t?.utilizationLinkText || "See the engagement dashboard's Utilization widget"}
            </Link>{' '}
            {t?.utilizationLinkSuffix || 'for weekly billable / utilized % by worker type.'}
          </p>
        </>
      )}
    </div>
  )
}
